import { AlertCircle } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"

/*
   The wrapper that makes a control accessible without anyone remembering
   to make it accessible. It generates the ids, and any control rendered
   inside picks up `id`, `aria-describedby`, `aria-invalid`, `aria-required`
   and `disabled` from context — so the wiring cannot be forgotten, and
   cannot drift when a field is copied to a new screen.

   1. HINT AND ERROR COEXIST. Most systems swap the hint out for the error.
      Here the hints carry domain rules that stay true when validation
      fails — "the printed date decides which master governs" is still the
      thing the reviewer needs to know while fixing a malformed date. Both
      are listed in aria-describedby, error last.

   2. OPTIONAL IS MARKED, NOT REQUIRED. Almost every field in this product
      is required, so an asterisk on nine fields out of ten is noise.
      `optional` renders the word; `required` only sets aria-required.
*/

interface FieldContextValue {
  id: string
  hintId?: string
  errorId?: string
  describedBy?: string
  invalid: boolean
  required: boolean
  disabled: boolean
}

const FieldContext = React.createContext<FieldContextValue | null>(null)

/**
 * Read the field wiring from the nearest FormField.
 * Returns null when a control is used standalone — then you own the aria yourself.
 */
export const useFormField = () => React.useContext(FieldContext)

/**
 * Merge field context onto a control's own props. Explicit props always win,
 * so a control can opt out of any single piece of the wiring.
 */
export function useFieldProps<
  T extends {
    id?: string
    "aria-describedby"?: string
    "aria-invalid"?: boolean | "true" | "false"
    required?: boolean
    disabled?: boolean
  },
>(props: T) {
  const field = useFormField()
  if (!field) return props

  return {
    ...props,
    id: props.id ?? field.id,
    "aria-describedby": props["aria-describedby"] ?? (field.describedBy || undefined),
    "aria-invalid": props["aria-invalid"] ?? (field.invalid || undefined),
    required: props.required ?? (field.required || undefined),
    disabled: props.disabled ?? (field.disabled || undefined),
  }
}

export interface FormFieldProps extends Omit<React.ComponentPropsWithoutRef<"div">, "children"> {
  /**
   * What the field is, in sentence case. Names the thing the person
   * recognises — "EPA Reg. No.", not "registration_number".
   */
  label: React.ReactNode
  /**
   * Something the field can't say for itself. Never a restatement of the
   * label, never an example that belongs in the placeholder.
   * Good: "The printed date decides which master governs."
   */
  hint?: React.ReactNode
  /**
   * Names the fix, not the failure.
   * Good: "Needs the company and product parts, like 65331-4."
   * Bad:  "Invalid format."
   */
  error?: React.ReactNode
  /** Renders the word "optional" beside the label. Prefer this to asterisks. */
  optional?: boolean
  /** Sets aria-required on the control. No visual marker — see `optional`. */
  required?: boolean
  /** Disables the control and mutes the whole field. */
  disabled?: boolean
  /** Supply your own id when integrating with react-hook-form or similar. */
  id?: string
  /**
   * Rendered flush-right in the label row — a character counter, a
   * "read from the label" affordance, a unit toggle. Keep it to one thing.
   */
  labelAction?: React.ReactNode
  children: React.ReactNode
}

export const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(function FormField(
  {
    className,
    label,
    hint,
    error,
    optional = false,
    required = false,
    disabled = false,
    id,
    labelAction,
    children,
    ...props
  },
  ref,
) {
  const generated = React.useId()
  const fieldId = id ?? `field-${generated}`
  const hintId = hint ? `${fieldId}-hint` : undefined
  const errorId = error ? `${fieldId}-error` : undefined

  // Order matters: the hint is context, the error is the correction.
  // Screen readers read describedby in order, so the fix comes last.
  const describedBy = [hintId, errorId].filter(Boolean).join(" ")

  const ctx = React.useMemo<FieldContextValue>(
    () => ({
      id: fieldId,
      hintId,
      errorId,
      describedBy,
      invalid: Boolean(error),
      required,
      disabled,
    }),
    [fieldId, hintId, errorId, describedBy, error, required, disabled],
  )

  return (
    <FieldContext.Provider value={ctx}>
      <div
        ref={ref}
        className={cn("flex flex-col gap-2", disabled && "opacity-60", className)}
        {...props}
      >
        <div className="flex items-baseline justify-between gap-3.5">
          <label htmlFor={fieldId} className="text-body-sm font-semibold text-text">
            {label}
            {optional && <span className="ml-1.5 font-normal text-text-3">optional</span>}
          </label>
          {labelAction}
        </div>

        {children}

        {hint && (
          <p id={hintId} className="text-caption leading-normal text-text-3">
            {hint}
          </p>
        )}

        {error && (
          <p
            id={errorId}
            // polite, not assertive: a reviewer tabbing through a form
            // should not have every field interrupt them mid-word
            role="status"
            aria-live="polite"
            className="flex items-start gap-1.5 text-caption leading-normal text-err"
          >
            <AlertCircle className="mt-px size-3.5 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </p>
        )}
      </div>
    </FieldContext.Provider>
  )
})

/*
   Fieldset — a group of related fields with one legend.
   Use when the fields only mean something together: the two halves of a
   registration number, a date range, "company + product + stamp date" on
   the Label Finder fallback. Not a substitute for spacing between
   unrelated fields.
*/

export interface FieldsetProps extends React.ComponentPropsWithoutRef<"fieldset"> {
  legend: React.ReactNode
  /** One line under the legend explaining what the group is for. */
  description?: React.ReactNode
}

export const Fieldset = React.forwardRef<HTMLFieldSetElement, FieldsetProps>(function Fieldset(
  { className, legend, description, children, ...props },
  ref,
) {
  return (
    <fieldset ref={ref} className={cn("min-w-0 border-0 p-0", className)} {...props}>
      <legend className="p-0 text-body-sm font-semibold text-text">{legend}</legend>
      {description && (
        <p className="mt-1 max-w-[64ch] text-caption text-text-3">{description}</p>
      )}
      <div className="mt-3.5">{children}</div>
    </fieldset>
  )
})
